import { Controller } from "@hotwired/stimulus"
import { TabulatorFull as Tabulator } from "tabulator-tables"
import { DateTime } from "luxon"

export default class extends Controller {
  static targets = [
    "filter",
    "table",
    "datepicker",
    "userselect"
  ]

  get modalController() {
    if (document.getElementById("userSwitchButton")) {
      return this.application.getControllerForElementAndIdentifier(
        document.getElementById("userSwitchButton"),
        "modals"
      )
    } else {
      return null
    }
  }

  // Creates a table and loads content via Ajax
  // Columns are are auto-generated from the JSON response
  connect() {
    if (
      document.documentElement.hasAttribute(
        "data-turbo-preview"
      )
    )
      return

    // Create columns with according formatter type
    // TODO: Make sorting more elegant, Should be set in markup
    let columns = []
    const locale = new URL(
      window.location.href
    ).pathname.split("/")[1]
    JSON.parse(this.data.get("cols")).forEach((element) => {
      // Default sort by string
      let sorter = "string",
        sorterParams = {}

      const dateIntervalSorter = function (
        a,
        b,
        aRow,
        bRow,
        column,
        dir,
        sorterParams
      ) {
        let durationA = DateTime.fromFormat(
          a.split(" - ")[0],
          "EEE, d. LLL. yyyy",
          { locale: locale }
        )
        let durationB = DateTime.fromFormat(
          b.split(" - ")[0],
          "EEE, d. LLL. yyyy",
          { locale: locale }
        )
        return durationA - durationB
      }

      // Some colums are sorted by date
      if (
        [
          "updated_at",
          "start_at",
          "end_at",
          "date",
          "valid_from",
          "valid_to",
          "period",
          "datetime"
        ].includes(element.field)
      ) {
        sorter = dateIntervalSorter

        // The column Duration -> duration is sorted by first datetime
      } else if (
        ["duration", "balance", "saldo"].includes(
          element.field
        )
      ) {
        sorter = function (
          a,
          b,
          aRow,
          bRow,
          column,
          dir,
          sorterParams
        ) {
          let durationA = Number(
            a
              .split("h")[0]
              .replace(".", "")
              .replace(",", ".")
          )
          let durationB = Number(
            b
              .split("h")[0]
              .replace(".", "")
              .replace(",", ".")
          )
          return durationA - durationB
        }

        // Some colums are sorted by number
      } else if ([""].includes(element.field)) {
        sorter = "number"
      }

      // Insert column into table
      columns.push({
        title: element.title,
        field: element.field,
        formatter: "html",
        width: element.width,
        sorter: sorter,
        sorterParams: sorterParams,
        visible: this.data.get("group") != element.field,
        headerSort: this.data.has("no-sort") == false
      })
    })

    // Add custom row formatting to vacation request lines.
    const rowFormatter = function (row) {
      let data = row.getData()
      switch (data["request_type"]) {
        case "vacation_request":
          row.getElement().style.fontStyle = "italic"
          row.getElement().style.backgroundColor = "#ffffff"
          row.getElement().style.backgroundSize = "8px 8px"
          row.getElement().style.borderBottom = "1px solid #ffffff"

          if (data["state_text"] != "declined") {
            row.getElement().style.backgroundImage =
              "repeating-linear-gradient(" +
              "-45deg, #dce8fa 0, #dce8fa 3px, #ffffff 0, #ffffff 50%)"
          } else {
            row
              .getElement()
              .style.setProperty(
                "background-color",
                "rgb(255, 236, 236)",
                "important"
              )
          }
          break

        case "vacation_shift":
          row.getElement().style.borderBottom = "1px solid #ffffff"
          row
            .getElement()
            .style.setProperty(
              "background-color",
              "#dce8fa",
              "important"
            )
          break

        case "time_compensation_request":
          row.getElement().style.fontStyle = "italic"
          row.getElement().style.backgroundColor = "#ffffff"
          row.getElement().style.backgroundSize = "8px 8px"
          row.getElement().style.borderBottom = "1px solid #ffffff"

          if (data["state_text"] != "declined") {
            row.getElement().style.backgroundImage =
              "repeating-linear-gradient(" +
              "-45deg, #f7e3cb 0, #f7e3cb 3px, #ffffff 0, #ffffff 50%)"
          } else {
            row
              .getElement()
              .style.setProperty(
                "background-color",
                "rgb(255, 236, 236)",
                "important"
              )
          }
          break

        case "time_compensation_shift":
          row.getElement().style.borderBottom = "1px solid #ffffff"
          row
            .getElement()
            .style.setProperty(
              "background-color",
              "#f7e3cb",
              "important"
            )
          break
      }
    }

    // Fill columns with data
    const id = "#" + this.tableTarget.getAttribute("id")
    console.log(this.data.get("initial-filter-field"))
    this.table = new Tabulator(id, {
      rowFormatter: rowFormatter,
      columns: columns,
      ajaxURL: this.data.get("content-url"),
      placeholder:
        this.data.get("placeholder") || "Keine Daten",
      layout: "fitColumns",
      selectableRows: false,
      pagination: "local",
      groupBy: this.data.get("group"),
      groupToggleElement: "header",
      printAsHtml: true,
      printStyled: true,
      htmlOutputConfig: { rowGroups: true },
      paginationSize: this.data.get("pagination") || 25,

      // We want to be able to disable sorting and set the direction
      initialSort: [
        this.data.has("no-sort")
          ? false
          : {
              column: columns[0].field,
              dir: this.data.has("sort-dir") || "asc"
            }
      ],
      initialFilter: this.data.get("initial-filter-field") ? [
        {field:this.data.get("initial-filter-field"), type:"like", value:this.data.get("initial-filter-value")}
      ] : [],
    })

    // Handle redirection on session timeout
    this.table.on("dataLoadError", function (error) {
      if (error.statusText == "Unauthorized") {
        location.reload()
      }
    })

    // Show loading indicator when data is loading
    this.table.on("dataLoading", function () {
      // Tabulator automatically shows the built-in loader
    })

    // Hide loading indicator when data is loaded
    this.table.on("dataLoaded", function () {
      // Tabulator automatically hides the built-in loader
      
      let table = this
      setTimeout(function () {
        let rows = Math.max(
          ...table.rowManager.displayRows.map(
            (el) => el.length
          )
        )
        table.footerManager.element.classList.toggle(
          "d-none",
          rows <= table.getPageSize()
        )
      }, 1)
    })
  }

  // Filters the table by the specified column
  // A filter can either be an input field or a select field
  filter() {
    let filters = []
    this.filterTargets.forEach((target) => {
      let value = "",
        type = ""

      // Filter type dropdown
      if (target.tagName == "SELECT") {
        value = target[target.selectedIndex].value
        if (target.getAttribute("data-mode") == "like")
          type = "like"
        else type = "="

        // Filter type input
      } else {
        value = target.value
        type = "like"
      }

      // Apply filter
      if (value != "") {
        filters.push(
          target
            .getAttribute("data-field")
            .split(",")
            .map((target) => {
              return {
                field: target,
                type: type,
                value: value
              }
            })
        )
      }
    })
    this.table.setFilter(filters)
  }

  // Resets the datepicker filter
  resetDatepicker(event) {
    this.datepickerTarget._flatpickr.clear()
  }

  // Updates orders table whenever the date filter changed
  // Nice thing is tabulatorjs takes care of synching with all other filters
  updatedTable(event) {
    const [data, status, xhr] = event.detail
    const response = JSON.parse(xhr.response)
    this.table.setData(response)
    const modal = this.modalController
    const userswitch =
      modal.context.targetObserver.targetsByName.valuesByKey.get(
        "userswitch"
      )

    window.history.pushState(
      {},
      "",
      location.href.replace(
        /(date=.+?)(&|$)/,
        "date=" +
          encodeURI(this.datepickerTarget.value) +
          "$2"
      )
    )

    for (const [value] of userswitch.entries()) {
      value.dataset.variables = JSON.stringify({
        date: this.datepickerTarget.value
      })
    }
  }

  // Handle state filter changes with AJAX reload
  filterState(event) {
    const target = event.target
    const value = target[target.selectedIndex].value
    const baseUrl = target.getAttribute("data-url")
    const url = new URL(baseUrl, window.location.origin)
    url.searchParams.set("state", value)
    console.log("Loading data from:", url.toString())
    this.table.setData(url.toString())
  }
}
